"use client";

import { useEffect, useState } from "react";
import { ATTACHED_TAPS_STORAGE_KEY } from "@/lib/claim-token";

interface AttachedTapsPayload {
  attachedCount: number;
  campaigns: Array<{ id: string; title: string; slug: string }>;
}

/**
 * Shows a one-time welcome banner when the user has just signed in/up and
 * we retroactively bound anonymous IYK taps from this browser to their account.
 *
 * The payload is written to sessionStorage by the signup/signin flows so it
 * survives the post-auth redirect. Cleared once read.
 */
export function AttachedTapsBanner() {
  const [payload, setPayload] = useState<AttachedTapsPayload | null>(null);

  useEffect(() => {
    let parsed: AttachedTapsPayload | null = null;
    try {
      const raw = sessionStorage.getItem(ATTACHED_TAPS_STORAGE_KEY);
      if (raw) {
        sessionStorage.removeItem(ATTACHED_TAPS_STORAGE_KEY);
        const maybe = JSON.parse(raw) as AttachedTapsPayload;
        if (maybe && maybe.attachedCount > 0) {
          parsed = maybe;
        }
      }
    } catch {
      // Malformed payload — silently drop it.
    }
    if (!parsed) return;
    // Syncing external state (sessionStorage) into React is a legitimate use
    // of setState in an effect; we only set once and the state is dismissed
    // from the user, so no cascading-render concern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPayload(parsed);
  }, []);

  if (!payload) return null;

  const { attachedCount, campaigns } = payload;
  const campaignCount = campaigns.length;
  const tapLabel = attachedCount === 1 ? "tap" : "taps";
  const campaignLabel = campaignCount === 1 ? "campaign" : "campaigns";

  return (
    <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
      <div
        role="status"
        className="flex items-start justify-between gap-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
      >
        <div>
          <p className="font-medium">Welcome — we linked your earlier activity.</p>
          <p className="mt-0.5">
            {attachedCount} prior {tapLabel} {attachedCount === 1 ? "was" : "were"} attached
            across {campaignCount} {campaignLabel}
            {campaigns.length > 0 ? ": " : "."}
            {campaigns.length > 0 && (
              <span className="font-medium">
                {campaigns.map((c) => c.title).join(", ")}
                {"."}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPayload(null)}
          className="text-green-800 underline hover:text-green-900"
          aria-label="Dismiss"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
